import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  getAllKontodelarBen,
  getAllKontodelar,
  trimTrailingDashes,
} from '../../../Kontering/Utils/KonteringUtils';
import {
  GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node,
  GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node__Associations_Konteringsforslag_All,
} from '../../Queries/_QueryTypes/GetKonteringHistory';
import { KonteringRecept_db_LRKonteringsrecept_All } from '../../Queries/_QueryTypes/KonteringRecept';
import LeverantorFakturaBildIkon from '../../../../../LeverantorFaktura/Components/LeverantorFakturaBildIkon';
import EDCheckbox from '../../../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EDTooltip from '../../../../../../../../../BaseComponents/Tooltip/EDTooltip';
import EDTypography from '../../../../../../../../../BaseComponents/Typography/EDTypography';
import { Tillaggsinfokod } from '../../../Kontering/Components/Tillaggsinfo/Utils/TillaggsinfoUtils';
import { TEDLRKonteringsForslagFI_REGISTRERASEnum } from '../../../../../../../../../globalQueryTypes';
import EDIconButton from '../../../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';

interface IKonteringProps {
  bill: GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node;
  disableKontering: boolean;
  index: number;
  kontering: GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node__Associations_Konteringsforslag_All;
  selectedKonteringar: GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node__Associations_Konteringsforslag_All[];
  setAdditionalInfo: React.Dispatch<
    React.SetStateAction<GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node__Associations_Konteringsforslag_All | null>
  >;
  setAdditionalInfoBillDate: React.Dispatch<React.SetStateAction<string>>;
  setAdditionalInfoOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedKonteringar: React.Dispatch<
    React.SetStateAction<
      GetKonteringHistory_node_TEDLRLeverantor__Associations_Fakturor_Connection_edges_node__Associations_Konteringsforslag_All[]
    >
  >;
  selectedRecept: KonteringRecept_db_LRKonteringsrecept_All[];
  testId: string;
}

const useStyles = makeStyles()((theme: Theme) => ({
  kontering: {
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',

    '&:hover': {
      cursor: 'pointer',
      border: `1px solid ${theme.customProperties.Fade.DarkOnLight.divider}`,
      borderRadius: '1px',
      margin: '-1px ',
    },
  },
  disabledKontering: {
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
  },
}));

const KonteringHistoryRow: React.FC<
  React.PropsWithChildren<IKonteringProps>
> = props => {
  const { classes } = useStyles();
  const kontering = props.kontering;
  const bill = props.bill;

  if (kontering == undefined) {
    return <span>Det finns inga konteringförslag</span>;
  }

  const kodstrangBenamningar = getAllKontodelarBen(kontering?._Associations);
  const kodstrangDelar = getAllKontodelar(kontering?._Associations);

  const codeStringText = trimTrailingDashes(
    kodstrangBenamningar.join('-')
  ).replaceAll('-', ' - ');

  const codeStringNumberWhole = trimTrailingDashes(
    kodstrangDelar.map(code => code?.KONTODEL || '').join('-')
  ).replaceAll('-', ' - ');

  const onClick = () => {
    if (props.disableKontering) {
      return;
    }
    const selectedKonteringarCopy = props.selectedKonteringar;
    if (props.selectedKonteringar.includes(kontering)) {
      props.setSelectedKonteringar(
        selectedKonteringarCopy.filter(
          filterKontering => filterKontering.id !== kontering.id
        )
      );
    } else {
      props.setSelectedKonteringar([...props.selectedKonteringar, kontering]);
    }
  };

  const registeredInventarium =
    (props.kontering?.FI_REGISTRERAS ??
      TEDLRKonteringsForslagFI_REGISTRERASEnum.EJ_ANGIVET) ===
    TEDLRKonteringsForslagFI_REGISTRERASEnum.JA;

  const showAdditionalInfoIcon =
    props.kontering.TILLAGGSINFO_KOD != null
      ? props.kontering.TILLAGGSINFO_KOD === Tillaggsinfokod.FI &&
        registeredInventarium
        ? true
        : props.kontering.TILLAGGSINFO_KOD !== Tillaggsinfokod.FI
      : false;

  return (
    <EDTooltip
      enterDelay={500}
      enterTouchDelay={500}
      key={props.index}
      title={
        props.disableKontering
          ? 'Man kan bara välja ett konteringsförslag'
          : undefined
      }
      enableClickOnTouch={props.disableKontering}
    >
      <div
        key={props.index}
        className={
          props.disableKontering ? classes.disabledKontering : classes.kontering
        }
        onClick={onClick}
        data-testid={props.testId}
      >
        <EDCheckbox
          checked={props.selectedKonteringar.includes(kontering)}
          marginRight={2}
          disabled={props.disableKontering}
          onCheck={onClick}
        />
        <span style={{ width: '100%' }}>
          <EDTypography
            variant="body1"
            bold
            color={props.disableKontering ? 'textSecondary' : 'textPrimary'}
          >
            {codeStringNumberWhole}
          </EDTypography>
          <EDTypography
            variant="body2"
            color={props.disableKontering ? 'textSecondary' : 'textPrimary'}
            style={{ fontSize: '0.75rem' }}
          >
            {codeStringText}
          </EDTypography>
        </span>
        {showAdditionalInfoIcon && (
          <EDIconButton
            tooltip="Visa konteringens tilläggsinformation"
            disabled={props.disableKontering}
            icon="additionalinfo_filled"
            margin="none"
            onClick={event => {
              props.setAdditionalInfo(kontering);
              props.setAdditionalInfoBillDate(bill.FAKTURADATUM);
              props.setAdditionalInfoOpen(true);
              event.stopPropagation();
            }}
            padding="dense"
          />
        )}
        <LeverantorFakturaBildIkon
          id={bill.id}
          disabled={props.disableKontering}
        />
      </div>
    </EDTooltip>
  );
};

export default KonteringHistoryRow;
